import axios, { AxiosInstance, AxiosRequestConfig, AxiosError, AxiosResponse } from "axios";
import { getApiBase } from "@/lib/apiBase";
import { isSuperadminHost } from "@/lib/superadminHost";
import { PLATFORM_ACCESS_KEY, PLATFORM_REFRESH_KEY } from "@/lib/tenantConfig";

const conf: AxiosRequestConfig = {
  baseURL: process.env.NEXT_PUBLIC_API_URI, 
  withCredentials: true, 
};

const httpClient: AxiosInstance = axios.create(conf);

httpClient.interceptors.request.use(
  (request) => {
    request.baseURL = getApiBase();
    if(typeof window !== 'undefined') {
      const subdomain = window.location.host.split('.')[0];
      request.headers['x-schema'] = subdomain;
    }
    if (
      process.env.NEXT_PUBLIC_NODE_ENV === 'development' &&
      typeof window !== 'undefined'
    ) {
      request.headers['Authorization'] = JSON.stringify({
        accessToken: localStorage.getItem('accessToken'),
        refreshToken: localStorage.getItem('refreshToken'),
        staff: encodeURIComponent(localStorage.getItem('staff') as string)
      })
    }

    return request;
  },
  (error) => {
    return Promise.reject(error);
  }
);



const refreshTokenRequest = async () => {
  const response = await httpClient.get("/auth/refresh", {
    headers: {
      'Authorization': JSON.stringify({
        accessToken: typeof window !== 'undefined' ?
          localStorage.getItem('accessToken')
          :
          '',
        refreshToken: typeof window !== 'undefined' ?
        localStorage.getItem('refreshToken')
        :
        '',
      })
    }
  }); 
  if(typeof window !== 'undefined') {
    localStorage.setItem('accessToken', response.data.data.accessToken);
    localStorage.setItem('refreshToken', response.data.data.refreshToken);
  }
  return response?.data;
};

let isRefreshing = false;
let endingSession = false;
let failedQueue: any[] = []; 

const processQueue = (error: AxiosError | null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

function isInvalidRefreshToken(error: AxiosError) {
  const data = error.response?.data as { message?: string } | undefined;
  return error.response?.status === 401 && data?.message === 'Invalid refresh token';
}

async function endSession() {
  if (typeof window === "undefined" || endingSession) return;
  endingSession = true;
  isRefreshing = false;
  if (isSuperadminHost()) {
    localStorage.removeItem(PLATFORM_ACCESS_KEY);
    localStorage.removeItem(PLATFORM_REFRESH_KEY);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('staff');
    if (window.location.pathname !== '/superadmin/login') {
      window.location.replace('/superadmin/login');
    }
    return;
  }
  try {
    await httpClient.get('/auth/sign-out');
  } catch {
    // Session is already invalid; still leave the dashboard.
  }
  localStorage.clear();
  window.location.href = '/auth/login';
}

httpClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response; 
  },
  async (error: AxiosError) => {
    if (endingSession) return Promise.reject(error);
    const originalRequest = error.config;
    const refreshFailed =
      originalRequest?.url?.includes('/auth/refresh') || isInvalidRefreshToken(error);

    if (refreshFailed && error.response) {
      await endSession();
      return Promise.reject(error);
    }
    
    //@ts-ignore
    if (error.response && error.response.status === 406) {
      await endSession();
      return Promise.reject(error);
    }
    //@ts-ignore
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      //@ts-ignore
      originalRequest._retry = true; 
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          //@ts-ignore
          originalRequest.headers['Authorization'] = 'Bearer ' + token; 
          //@ts-ignore
          return httpClient(originalRequest);
        });
      }
      //@ts-ignore
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshTokenRequest();
        httpClient.defaults.headers.common['Authorization'] = 'Bearer ' + newToken; 
        processQueue(null); 
        //@ts-ignore
        return httpClient(originalRequest);
      } catch (refreshError) {
        //@ts-ignore
        processQueue(refreshError); 
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export { httpClient, conf };
